import { Injectable } from '@angular/core';
import { Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Departments } from '@app/configs/app.config';
import { environment } from '@environment/environment';
import { Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { AppROUTES } from '@config/app.config';
import { AuthService } from '@core/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
  ) {}

  /* Guard to protect routes based on authentication and authorization.*/
  canActivate(state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    if (
      state.url === AppROUTES.LOGOUT ||
      state.url === AppROUTES.UNAUTHORIZED
    ) {
      return of(true);
    }

    if (this.isAuthCallbackUrl(state.url)) {
      return of(true);
    }

    return this.authService.authReady$.pipe(
      filter((ready): ready is boolean => ready !== null),
      take(1),
      map((ready) => {
        if (!ready || !this.authService.hasValidToken()) {
          return false;
        }

        const claims = this.authService.getClaims();
        return this.checkRoles(claims);
      }),
    );
  }

  /* Check if the user's roles from the token claims include the required role for the active department. If not, redirect to unauthorized page. */
  private checkRoles(claims: any): boolean | UrlTree {
    const roles: string[] = claims?.roles || [];
    const origin = this.resolveOrigin(window.location.origin);

    const activeDepartment =
      origin === this.resolveOrigin(environment.auth.scienceReady.scienceReadyUrl)
        ? Departments.SCIENCE_READY
        : Departments.SITE_READY;

    if (roles.includes(activeDepartment)) {
      return true;
    }

    return this.router.createUrlTree([AppROUTES.UNAUTHORIZED]);
  }

  private isAuthCallbackUrl(url: string): boolean {
    return url.includes('code=') && url.includes('state=');
  }

  private resolveOrigin(origin: string | undefined): string {
    return (origin || '').trim().replace(/\/+$/, '');
  }
}
